'use client';

import React, { memo, useMemo } from 'react';

import { MetaType } from '@repo/types';
import { Spinner } from '@repo/ui/components/spinner';
import {
  CommandEmpty,
  CommandGroup,
  CommandList,
  CommandSeparator,
} from '@repo/ui/components/ui/command';
import { cn } from '@repo/ui/lib/utils';

import { BrandListItem } from '@/hooks/api/brand/model';

import { AdditionalItemType } from '.';
import { AppNavigatorListItem } from './app-navigator-list-item';

const MIN_ITEMS_FOR_SCROLL = 10;

interface AppNavigatorListProps {
  additionalItems: AdditionalItemType[];
  brand?: BrandListItem;
  selectBrand: (brand: BrandListItem) => void;
  brands: BrandListItem[];
  meta?: MetaType;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  fetchMoreOnScroll: (
    containerRefElement: React.UIEvent<HTMLDivElement>,
  ) => void;
}

const PureAppNavigatorList: React.FC<AppNavigatorListProps> = ({
  additionalItems,
  brand,
  selectBrand,
  brands,
  meta,
  isFetching,
  isFetchingNextPage,
  fetchMoreOnScroll,
}) => {
  // Memoize empty state text
  const emptyStateText = useMemo(() => {
    if (isFetching && !isFetchingNextPage) return 'Loading...';
    return 'No brands found';
  }, [isFetching, isFetchingNextPage]);

  // Memoize list height calculation
  const listClassName = useMemo(() => {
    const shouldScroll =
      brands.length >= MIN_ITEMS_FOR_SCROLL &&
      meta?.take === MIN_ITEMS_FOR_SCROLL;
    const heightClass = shouldScroll
      ? 'max-h-[min(calc(var(--cmdk-list-height)-10px),50vh)]'
      : 'max-h-[50vh]';

    return cn('px-[5px] cred-md:px-1.5 cred-xl:px-2', heightClass);
  }, [brands.length, meta?.take]);

  // Memoize if we should show additional items
  const hasAdditionalItems = useMemo(
    () => additionalItems && additionalItems.length > 0,
    [additionalItems],
  );

  return (
    <CommandList onScroll={fetchMoreOnScroll} className={listClassName}>
      <CommandEmpty>
        <div className="flex w-full items-center justify-center gap-2 cred-md:gap-2.5 cred-lg:gap-3 cred-xl:gap-2.5">
          {isFetching && !isFetchingNextPage && <Spinner size={20} />}
          <span className="text-center text-sm text-black cred-md:text-base cred-lg:text-base cred-xl:text-base">
            {emptyStateText}
          </span>
        </div>
      </CommandEmpty>

      {hasAdditionalItems && (
        <>
          <CommandGroup className="p-0">
            {additionalItems.map((item) => (
              <AppNavigatorListItem
                key={item.value.id}
                type="fixed"
                brand={item.value}
                label={item.label}
                isSelected={item.value.id === brand?.id}
                onClick={() => item.onClick?.(item.value)}
              />
            ))}
          </CommandGroup>
          <CommandSeparator />
        </>
      )}

      <CommandGroup className="p-0">
        {brands.map((brandItem) => (
          <AppNavigatorListItem
            key={brandItem.id}
            type="dynamic"
            brand={brandItem}
            isSelected={brandItem.id === brand?.id}
            onClick={() => selectBrand(brandItem)}
          />
        ))}
        {isFetchingNextPage && (
          <div className="flex w-full items-center justify-center gap-2 py-2">
            <Spinner size={16} />
            <span className="text-xs text-black">Loading more...</span>
          </div>
        )}
      </CommandGroup>
    </CommandList>
  );
};

export const AppNavigatorList = memo(PureAppNavigatorList);
